
<template lang="pug">
  .yezza-shipping
    v-layout.my-6(v-if="isFetching")
      v-row.fill-height.ma-0(align='center' justify='center')
        v-progress-circular.ma-auto(:width="2" :size="50" color="primary" indeterminate)

    v-container.pa-0.pb-12(v-else style="max-width:600px")

      div.zooPurple.d-flex.align-center.pa-3(:style="$vuetify.breakpoint.smAndUp ? 'border-radius: 4px; margin-top: 8px; margin-bottom: 8px;' : ''")
        div.c1.white--text {{ $t('alert_feedback.title') }}
        v-spacer
        v-btn.button-Aa-small.pa-0.ml-3(color="transparent" text small @click="openFeedback")
          div.white--text {{ $t('alert_feedback.action') }}

      .order-settings
        v-card.pa-4.ma-4(v-if="!orderSettings" flat style="border:1px solid var(--v-basic400);")
          v-row(no-gutters)
            v-col
              div.s1.basic800--text {{ $t('local_order_settings') }}
              div.c1.basic600--text.mt-1 {{ $t('setup_hours_scheduling') }}
                strong  {{ $t('local_delivery') }}
            v-col.pl-4.shrink
              v-btn.button-AA-tiny(color="info500" depressed dark small height="24" @click="openLocalOrderSettingsSetup = true") {{ $t('setup') }}
          
        v-card.py-4.ma-4(v-else flat style="border:1px solid var(--v-basic400);")
          .header.px-4
            v-row(align='center')
              v-col
                .s1.basic800--text {{ $t('local_order_management') }}
                .c1.basic700--text.mt-2 {{ $t('local_order_management_text_1') }}
                .c2.basic700--text {{ $t('local_order_management_text_2') }}
              v-col.shrink
                y-switch.mr-n2(:color="$vuetify.theme.themes[$theme].success400" v-model="orderSettings.enabled" @change="updateOrderManagementSettings()")
          
          div.d-flex.px-4.py-1.mt-2(:style="{'background-color': $vuetify.theme.themes[$theme].basic600T08}")
            .s2.basic600--text.text-uppercase {{ $t('hours') }}
            v-spacer
            a.button-Aa-small.my-auto.info500--text(@click="openLocalOrderSettings = true") {{ $t('edit') }}

          .hours.px-4.mt-4(v-if="businessHours")
            .s2 {{ businessHours.use_same_hours ? 'Pickup & delivery hours' : 'Delivery hours'}}
            div.d-flex.mt-2
              .day(style="min-width:100px")
                .p2.basic600--text {{ $t('day') }}
              .hour
                .p2.basic600--text {{ $t('hours') }}
              v-spacer
              .limit.text-center(style="min-width:100px")
                .p2.basic600--text {{ $t('order_limit') }}
            
            div.d-flex.mt-2(v-for="day in hourDays" :key="day.name + 'Delivery'")
              .day(style="min-width:100px")
                .p2 {{ day.display | capitalize }}
              .hour
                .p2(v-if="getDayHours(businessHours.delivery_hours, day.name).length == 0") {{ $t('closed') }}
                .p2(v-for="hour in getDayHours(businessHours.delivery_hours, day.name)" :key="JSON.stringify(hour)") {{ formatTime(hour.begin) }} - {{ formatTime(hour.end) }}
              v-spacer
              .limit.text-center(style="min-width:100px")
                .p2 {{ getDayLimit(businessHours.delivery_hours, day.name) }}
            
            .pickup(v-if="!businessHours.use_same_hours")
              v-divider.basic300.my-4
              .s2 {{ $t('pickup') }}
              div.d-flex.mt-2
                .day(style="min-width:100px")
                  .p2.basic600--text {{ $t('day') }}
                .hour
                  .p2.basic600--text {{ $t('hours') }}
                v-spacer
                .limit.text-center(style="min-width:100px")
                  .p2.basic600--text {{ $t('order_limit') }}
              
              div.d-flex.mt-2(v-for="day in hourDays" :key="day.name + 'Delivery'")
                .day(style="min-width:100px")
                  .p2 {{ day.display | capitalize }}
                .hour
                  .p2(v-if="getDayHours(businessHours.pickup_hours, day.name).length == 0") {{ $t('closed') }}
                  .p2(v-for="hour in getDayHours(businessHours.pickup_hours, day.name)" :key="JSON.stringify(hour)") {{ formatTime(hour.begin) }} - {{ formatTime(hour.end) }}
                v-spacer
                .limit.text-center(style="min-width:100px")
                  .p2 {{ getDayLimit(businessHours.pickup_hours, day.name) }}
          
          v-divider.basic300.my-4

          .restricted-dates.px-4
            .s2 {{ $t('restricted_dates') }}
            .p2.mt-2
              div(v-if="orderSettings.restricted_dates.length > 0")
                span.mr-2(v-for="(date, index) in orderSettings.restricted_dates")
                  span {{ date | humanizeDate }}
                  span(v-if="index != orderSettings.restricted_dates.length - 1") ,
              div(v-else) -
          
          div.d-flex.px-4.py-1.my-4(:style="{'background-color': $vuetify.theme.themes[$theme].basic600T08}")
            .s2.basic600--text.text-uppercase {{ $t('order_settings') }}
            v-spacer
            a.button-Aa-small.my-auto.info500--text(@click="showUpdateOrdersettings = true") {{ $t('edit') }}

          .order-settings(v-if="orderSettings")
            .scheduling.px-4
              .s2 {{ $t('scheduling') }}
              .p2.mt-2
                span(v-if="orderSettings.scheduling_enabled") {{ $t('allow_scheduling_in_advance', { begin: orderSettings.scheduling_begin_time_value, unit: orderSettings.scheduling_begin_time_unit } ) }}
                span(v-else) {{ $t('disabled') }}

            v-divider.basic300.my-4

            .preparation-time.px-4
              .s2 {{ $t('preparation_time') }}
              .p2.mt-2
                span(v-if="orderSettings.preparation_time_enabled") {{ $t('to_prepare_order', { time: orderSettings.preparation_time_value, unit: orderSettings.preparation_time_unit }) }}
                span(v-else) {{ $t('disabled') }}

            v-divider.basic300.my-4
            
            .cut-off-times.px-4
              .s2 {{ $t('cut_off_times') }}
              .p2.mt-2
                span(v-if="orderSettings.cut_off_time_enabled") {{ $t('mins_before_closing', { time: orderSettings.cut_off_time }) }}
                span(v-else) {{ $t('disabled') }}

      .min-order-amount
        v-card.pa-4.ma-4(v-if="!minOrder" flat style="border:1px solid var(--v-basic400);")
          v-row(no-gutters)
            v-col
              div.s1.basic800--text {{ $t('minimum_order_amount') }}
              div.c1.basic600--text.mt-1 {{ $t('set_minimum_order_amount') }}
            v-col.pl-4.shrink
              v-btn.button-AA-tiny(color="info500" depressed dark small height="24" @click="editMinOrderAmount = minOrderAmount; openEditMinOdrderAmountDialog = true") {{ $t('setup') }}
        v-card.pa-4.ma-4(v-else flat style="border:1px solid var(--v-basic400);")
          .s1 {{ $t('minimum_order_amount') }}
          .c1.basic600--text {{ $t('set_minimum_order_amount') }}
          v-row(align='center')
            v-col
              .p2.basic700--text {{ $t('enable_min_order_amount') }}
            v-col.shrink
              y-switch(:color="$vuetify.theme.themes[$theme].success400" v-model="enableMinOrderAmount" @change="updateMinOrderAmount()")
          
          .enable(v-if="enableMinOrderAmount")
            v-divider.basic300
            div.d-flex.py-1.mt-2
              .p2.textColorDefault--text {{ minOrder.minimum_order_amount | displayPrice }}
              v-spacer
              .button-Aa-small.my-auto.info500--text(@click="editMinOrderAmount = minOrderAmount; openEditMinOdrderAmountDialog = true") {{ $t('edit') }}

    .modals
      local-order-settings-setup(v-if="openLocalOrderSettingsSetup" :open.sync="openLocalOrderSettingsSetup" :company.sync="company" @save="fetchOrderManagementSettings()")
      local-order-settings(v-if="openLocalOrderSettings" :open.sync="openLocalOrderSettings" :company.sync="company" :settings.sync="orderSettings" @edited="fetchOrderManagementSettings()")
      order-settings(v-if="showUpdateOrdersettings" :open.sync="showUpdateOrdersettings" :company.sync="company" @edited="fetchOrderManagementSettings()")
      y-dialog(v-model='openEditMinOdrderAmountDialog', max-width='320' persistent)
        v-card
          v-card-title
            .h6.textColorDefault--text {{ $t('edit_minimum_order_amount') }}
          v-card-text
            .label.basic600--text.text-uppercase {{ $t('minimum_order_amount') }}
            v-text-field.mt-2(v-model='editMinOrderAmount' placeholder="0" background-color="basic200" outlined dense height="40" type="number" :rules="rules.has_quantity" :prefix="$currency.symbol" hide-details="auto")
            .c1.basic600--text.mt-2 {{ $t('only_orders_over_this_amount') }}
          v-card-actions
            v-spacer
            v-btn(text @click='openEditMinOdrderAmountDialog = false; ' depressed height=32 )
              .button-Aa-small.primary400--text {{ $t('cancel') }}
            v-btn.mr-2(color='textColorAccent' depressed height=32 @click="updateMinOrderAmount" :loading="isLoading")
              .button-AA-small.basic100--text {{ $t('save') }}

</template>

<script>
import { mapActions } from 'vuex'
import LocalOrderSettingsSetup from '@/components/dialogs/ordersettings/LocalOrderSettingsSetup.vue'
import LocalOrderSettings from '@/components/dialogs/ordersettings/LocalOrderSettings.vue'
import OrderSettings from '@/components/dialogs/ordersettings/OrderSettings.vue'

export default {
  name: 'OrderManagementSettings',
  components: { LocalOrderSettingsSetup, LocalOrderSettings, OrderSettings },
  props: ['company'],
  data () {
    return {
      isFetching: false,
      openLocalOrderSettingsSetup: false,
      openLocalOrderSettings: false,
      showUpdateOrdersettings: false,
      openEditMinOdrderAmountDialog: false,
      businessHours: null,
      orderSettings: null,
      minOrder: null,
      editMinOrderAmount: 0,
      isLoading: false,
      hourDays: [
        { name: 'Monday', display: 'mon' },
        { name: 'Tuesday', display: 'tue' },
        { name: 'Wednesday', display: 'wed' },
        { name: 'Thursday', display: 'thu' },
        { name: 'Friday', display: 'fri' },
        { name: 'Saturday', display: 'sat' },
        { name: 'Sunday', display: 'sun' }
      ],
      rules: {
        has_quantity: [(v) => !!v || 'This field is required', (v) => v != 0|| 'This field is required']
      }
    }
  },
  computed: {
    enableMinOrderAmount: {
      get() {
        if (this.minOrder) {
          return this.minOrder.minimum_order_amount_enabled
        }
        return false
      },
      set(newValue) {
        this.minOrder.minimum_order_amount_enabled  = newValue
      }
    },
    minOrderAmount: {
      get() {
        if (this.minOrder) {
          return this.minOrder.minimum_order_amount/this.$currency.multiplier
        }
        return false
      },
      set(newValue) {
        this.minOrder.minimum_order_amount  = (parseFloat(newValue)).toFixed(this.$currency.exponent).replace('.', '')
      }
    }
  },
  mounted () {
    this.fetchOrderManagementSettings()
    this.fetchMinOrder()
  },
  methods: {
    ...mapActions([
      'showMessage'
    ]),
    fetchOrderManagementSettings() {
      this.isFetching = true
      this.$axios.get(`/oms/${this.company.slug}/`)
        .then((response) => {
          this.orderSettings = response.data
          this.fetchBusinessHours()
        })
        .catch(() => {
          this.isFetching = false
        })
    },
    fetchBusinessHours() {
      this.$axios.get(`/oms/${this.company.slug}/business_hours`)
        .then((response) => {
          const data = response.data
          const businessHours = {}
          businessHours.pickup_hours = (data.filter(f => f.type_name == 'pickup')[0] || { days: [] }).days
          businessHours.delivery_hours =  (data.filter(f => f.type_name == 'delivery')[0] || { days: [] }).days
          businessHours.use_same_hours = JSON.stringify(businessHours.pickup_hours) === JSON.stringify(businessHours.delivery_hours)
          this.businessHours = businessHours
        })
        .catch(() => {})
        .finally(() => {
          this.isFetching = false
        })
    },
    fetchMinOrder() {
      this.$axios.get(`/oms/${this.company.slug}/minimum_order`)
        .then((response) => {
          this.minOrder = { ...response.data }
        }).catch(() => {})
    },
    formatTime (time24) {
      let ts = time24
      const H = + ts.substr(0, 2)
      let h = (H % 12) || 12
      h = (h < 10) ? ('0' + h) : h
      const ampm = H < 12 ? 'am' : 'pm'
      ts = h + ':' + ts.substr(3, 2) + ampm
      return ts
    },
    getDayHours (hours, day) {
      const result = hours.find( g => g.day === day)
      if (!result) {
        return []
      }
      return result.hours
    },
    getDayLimit (hours, day) {
      const result = hours.find(g => g.day === day)
      if (!result) {
        return '-'
      }
      return result.order_limit_enabled ? result.order_limit || '-' : '-'
    },
    updateOrderManagementSettings () {
      this.$axios.patch(`/oms/${this.company.slug}/`, this.orderSettings).catch(() => {})
    },
    updateMinOrderAmount () {
      this.isLoading = true
      if (this.minOrder && this.minOrder.id) {
        const params = { ...this.minOrder }
        if (this.openEditMinOdrderAmountDialog) {
          params.minimum_order_amount = (parseFloat(this.editMinOrderAmount)).toFixed(this.$currency.exponent).replace('.', '')
        }
        this.$axios.patch(`/oms/${this.company.slug}/minimum_order/${this.minOrder.id}`, params)
          .then((response) => {
            this.minOrder = response.data
            this.openEditMinOdrderAmountDialog = false
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      } else {
        const params = {
          minimum_order_amount_enabled: true,
          minimum_order_amount: (parseFloat(this.editMinOrderAmount)).toFixed(this.$currency.exponent).replace('.', '')
        }
        this.$axios.post(`/oms/${this.company.slug}/minimum_order`, params)
          .then((response) => {
            this.minOrder = { ...response.data }
            this.openEditMinOdrderAmountDialog = false
          })
          .catch(() => {
            this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
          })
          .finally(() => {
            this.isLoading = false
          })
      }
    },
    openFeedback () {
      window.open(`https://yezzaio.typeform.com/to/GWrwEAYT#id=${this.company.id}`)
    }
  }
}
</script>

<style scoped>
.v-text-field--outlined >>> fieldset {
  border-color: var(--v-primary200);
}
</style>

<i18n>
{
  "en": {
    "alert_feedback": {
      "title": "Let us know what you think about the new order settings",
      "action": "Leave feedback"
    },
    "local_order_settings": "Local order settings",
    "setup_hours_scheduling": "Setup hours, scheduling, preparation time, cut-off time and restricted dates for",
    "local_delivery": "local delivery and store pickup.",
    "setup": "Setup",
    "local_order_management": "Local order management",
    "local_order_management_text_1": "Manage how you accept orders for",
    "local_order_management_text_2": "store pickup and local delivery",
    "hours": "Hours",
    "minimum_order_amount": "Minimum order amount",
    "set_minimum_order_amount": "Set minimum order amount for checkout.",
    "enable_min_order_amount": "Enable min order amount",
    "edit_minimum_order_amount": "Edit minimum order amount",
    "only_orders_over_this_amount": "Only orders over this amount will be eligible to checkout",
    "allow_scheduling_in_advance": "Allow scheduling {begin} {unit} in advance",
    "order_limit": "Order limit",
    "restricted_dates": "Restricted dates",
    "preparation_time": "Preparation time",
    "to_prepare_order": "{time} {unit} to prepare order",
    "cut_off_times": "Cut-off times",
    "mins_before_closing": "{time} mins before closing"
  },
  "id": {
    "local_order_settings": "Pengaturan pesanan lokal",
    "setup_hours_scheduling": "Setup waktu, penjadwalan, persiapan, batasan waktu, batasan tanggal untuk",
    "local_delivery": "pengiriman lokal dan pengambilan di toko.",
    "setup": "Atur",
    "local_order_management": "Pengelolaan order lokal",
    "local_order_management_text_1": "Kelola bagaimana kamu mengambil order untuk",
    "local_order_management_text_2": "pengambilan di toko dan pengiriman lokal",
    "hours": "Jam",
    "minimum_order_amount": "Jumlah pesanan minimum",
    "set_minimum_order_amount": "Set minimum order amount for checkout.",
    "enable_min_order_amount": "Aktifkan jumlah pesanan minimum",
    "edit_minimum_order_amount": "Edit jumlah pesanan minimum",
    "only_orders_over_this_amount": "Hanya pesanan di atas jumlah ini yang memenuhi syarat untuk pembayaran",
    "allow_scheduling_in_advance": "Izinkan penjadwalan {begin} {unit} sebelumnya",
    "order_limit": "Batas pesanan",
    "restricted_dates": "Tanggal terbatas",
    "preparation_time": "Waktu persiapan",
    "to_prepare_order": "{time} {unit} untuk menyiapkan pesanan",
    "cut_off_times": "Waktu cut-off",
    "mins_before_closing": "{time} menit sebelum tutup"
  }
}
</i18n>